/**
 * Vérification d'une écriture de forme de PARAGRAPHE : la forme relue dans le
 * fichier exporté après l'écriture, confrontée à ce que l'agent a demandé
 * (#1866, spec #1858, règle commune n°4). Pendant de `verificationDeForme.ts`,
 * qui juge des passages de texte : ici l'unité jugée, comptée et citée en
 * exemple est le paragraphe.
 *
 * Module pur. Ce que « conforme » veut dire pour chaque réglage — et sa
 * tolérance numérique — est dit par `reglagesDeParagraphe.ts`.
 */

import {
  CANVAS_FORMAT_DIFFERENCE_EXAMPLES_MAX,
  CANVAS_FORMAT_DIFFERENCE_TEXT_MAX,
  CANVAS_FORMAT_REMAINING_MAX,
  CANVAS_PARAGRAPH_SETTING_KEYS,
  type CanvasFormatSettingReceipt,
  type CanvasFormatSettingValue,
  type CanvasFormatUnreadableReason,
} from '@hector/shared-types';
import { resumerLaBordure, type FormeDeParagraphe, type Reglage } from '@hector/docx-forme';
import { jugerLeReglageDeParagraphe, type ReglagesDeParagraphe, type VerdictDeParagraphe } from './reglagesDeParagraphe';
import { formeRendue, resteALaMain, type RaisonDeLaisser } from './remiseAZero';
import type { RelectureDeLActe } from './verificationDeForme';

/**
 * Un paragraphe visé dont certains réglages demandés n'ont pas été écrits,
 * parce que l'écriture aurait effacé des attributs posés à la main — ou, pour
 * une remise à zéro, une révision en attente.
 */
export interface ParagrapheLaisse {
  nodeId: string;
  /** Absent : des attributs posés à la main seraient perdus. Sinon, pourquoi une remise à zéro n'écrit pas ce paragraphe (`remiseAZero.ts`). */
  raison?: RaisonDeLaisser;
  /** Les réglages demandés qui restent tels quels sur ce paragraphe. */
  reglages: readonly string[];
  /** Les attributs que l'écriture aurait effacés. */
  attributs: readonly string[];
  /** Vrai si ses autres écritures ont toutes été appliquées (ou s'il n'en avait pas). */
  ecrit: boolean;
}

/**
 * Juge, réglage par réglage, ce que le fichier relu porte sur les paragraphes
 * écrits.
 *
 * @param relecture La forme relue dans le fichier exporté APRÈS l'écriture, ou
 *   la raison pour laquelle ce fichier ne juge pas l'écriture : aucun réglage
 *   n'est alors dit conforme ni différent.
 * @param paragraphes Ce que la cible désigne et qui est entièrement écrit (ou
 *   montrait déjà la forme), dans l'ordre du document.
 * @param demandes Les réglages demandés. Les réglages absents ne sont pas jugés.
 * @param paragraphesNonEcrits Paragraphes visés dont une écriture au moins
 *   manque. Ils ne sont pas relus : chaque réglage les compte comme non jugés
 *   (`not_written`), donc aucun ne sort conforme pour la demande entière.
 * @param laisses Paragraphes dont certains réglages demandés n'ont pas été
 *   écrits (`ecrituresEmpechees`). Leurs autres réglages sont relus et jugés
 *   comme ceux des autres paragraphes ; les réglages laissés sont comptés non
 *   jugés (`attributes_would_be_lost`), donc jamais conformes pour la demande.
 * @returns Un reçu par réglage demandé, dans l'ordre du contrat partagé : le style nommé en tête, les fonds puis le cadre de case en dernier.
 */
export function verifierLaFormeDeParagraphe(
  relecture: RelectureDeLActe,
  paragraphes: ReadonlyArray<{ nodeId: string }>,
  demandes: ReglagesDeParagraphe,
  paragraphesNonEcrits = 0,
  laisses: ReadonlyArray<ParagrapheLaisse> = [],
): CanvasFormatSettingReceipt[] {
  const relus = 'acte' in relecture ? new Map(relecture.acte.paragraphes.map((lu) => [lu.nodeId, lu])) : undefined;

  /** Un fond se juge sur la couleur relue, quelle que soit son origine : celle du paragraphe, ou celle de sa cellule de tableau. Le cadre d'une case, de même, sur ses côtés relus. */
  const jugerLaValeurRelue = (demande: CanvasFormatSettingValue, lu: Reglage<string> | undefined): VerdictDeParagraphe => {
    // Sans cellule relue, le paragraphe n'est plus dans un tableau : rien n'est jugé.
    if (lu === undefined || !lu.lisible) return { issue: 'non_lisible' };
    return lu.valeur === demande ? { issue: 'conforme' } : { issue: 'different', lu: lu.valeur };
  };
  /** Le style nommé se juge sur l'identifiant que le paragraphe relu porte ; les autres réglages, sur leur valeur résolue. */
  const juger = (setting: (typeof CANVAS_PARAGRAPH_SETTING_KEYS)[number], demande: CanvasFormatSettingValue, lu: FormeDeParagraphe): VerdictDeParagraphe => {
    if (setting === 'shading') return jugerLaValeurRelue(demande, lu.fond);
    if (setting === 'cellShading') return jugerLaValeurRelue(demande, lu.cellule?.fond);
    // Le cadre d'une case se juge sur ses côtés tracés, quelle qu'en soit l'origine, dans les mots de `border`.
    if (setting === 'cellBorder') return jugerLaValeurRelue(demande, lu.cellule === undefined ? undefined : resumerLaBordure(lu.cellule.cadre).border);
    if (setting !== 'styleId') return jugerLeReglageDeParagraphe(setting, demande, lu.paragraphe[setting]);
    return lu.styleId === demande ? { issue: 'conforme' } : { issue: 'different', lu: lu.styleId ?? 'none' };
  };

  return CANVAS_PARAGRAPH_SETTING_KEYS.flatMap((setting): CanvasFormatSettingReceipt[] => {
    const requested = demandes[setting];
    if (requested === undefined) return [];

    const exemples: Array<{ nodeId: string; text?: string; read: CanvasFormatSettingValue }> = [];
    let ecarts = 0;
    let nonLus = 0;
    let raison: CanvasFormatUnreadableReason | undefined;
    const nonLu = (cause: CanvasFormatUnreadableReason): void => {
      nonLus += 1;
      raison ??= cause;
    };

    const laissesPourCeReglage = laisses.filter((laisse) => laisse.reglages.includes(setting));
    const autresReglagesDesLaisses = laisses.filter((laisse) => !laisse.reglages.includes(setting));
    // Un paragraphe laissé dont une interruption a empêché les autres écritures n'a rien à relire non plus.
    const laissesNonEcrits = autresReglagesDesLaisses.filter((laisse) => !laisse.ecrit).length;
    for (const { nodeId } of [...paragraphes, ...autresReglagesDesLaisses.filter((laisse) => laisse.ecrit)]) {
      if ('nonRelu' in relecture) { nonLu(relecture.nonRelu); continue; }
      const lu = relus?.get(nodeId);
      if (lu === undefined) { nonLu('target_not_found_on_reread'); continue; }
      const verdict = juger(setting, requested, lu);
      if (verdict.issue === 'non_lisible') { nonLu('cascade_not_resolved'); continue; }
      if (verdict.issue === 'conforme') continue;
      ecarts += 1;
      if (exemples.length < CANVAS_FORMAT_DIFFERENCE_EXAMPLES_MAX) {
        const debut = lu.passages.map((passage) => passage.texte).join('').slice(0, CANVAS_FORMAT_DIFFERENCE_TEXT_MAX);
        exemples.push({ nodeId, ...(debut === '' ? {} : { text: debut }), read: verdict.lu });
      }
    }

    if (paragraphesNonEcrits + laissesNonEcrits > 0) {
      nonLus += paragraphesNonEcrits + laissesNonEcrits;
      raison ??= 'not_written';
    }
    if (laissesPourCeReglage.length > 0) {
      nonLus += laissesPourCeReglage.length;
      raison ??= 'attributes_would_be_lost';
    }

    const [premier, ...autres] = exemples;
    return [{
      setting,
      requested,
      outcome: ecarts > 0 ? 'different' : nonLus > 0 ? 'unreadable' : 'conforming',
      ...(premier === undefined ? {} : { different: { count: ecarts, examples: [premier, ...autres] } }),
      ...(raison === undefined ? {} : { unreadable: { count: nonLus, reason: raison } }),
    }];
  });
}

/**
 * Juge une remise à zéro (`reset`) sur l'ORIGINE de la forme : un paragraphe
 * est conforme quand le fichier relu n'y porte plus rien de posé à la main.
 *
 * @param paragraphes Les paragraphes remis à zéro, ou qui n'avaient rien à
 *   remettre, dans l'ordre du document.
 * @param paragraphesNonEcrits Paragraphes visés dont une écriture manque : non jugés (`not_written`).
 * @param laisses Paragraphes laissés tels quels, non jugés, chacun compté sous sa raison (`pending_tracked_change`, `outline_level_not_restorable`).
 * @param texteLaisse Paragraphes remis à zéro dont la forme de TEXTE a été laissée exprès : jugés sur leur seule forme de paragraphe.
 * @returns Le reçu du réglage `reset` : `different` nomme ce qui reste posé à
 *   la main et que la remise à zéro retire (éléments du fichier, `pPr/…` puis
 *   `rPr/…` ; ce que l'éditeur ne sait pas retirer n'en fait pas partie, il est
 *   compté par `format.reset.notRemoved`) ; `remaining` dit la
 *   forme de paragraphe que montrent les paragraphes conformes, et le style
 *   nommé qui la donne.
 */
export function verifierLaRemiseAZero(
  relecture: RelectureDeLActe,
  paragraphes: ReadonlyArray<{ nodeId: string }>,
  paragraphesNonEcrits = 0,
  laisses: ReadonlyArray<ParagrapheLaisse> = [],
  texteLaisse: ReadonlySet<string> = new Set(),
): CanvasFormatSettingReceipt[] {
  type FormeRestante = Extract<NonNullable<CanvasFormatSettingReceipt['remaining']>[number], { paragraphs: number }>;
  const relus = 'acte' in relecture ? new Map(relecture.acte.paragraphes.map((lu) => [lu.nodeId, lu])) : undefined;
  const exemples: Array<{ nodeId: string; text?: string; read: CanvasFormatSettingValue }> = [];
  const restes = new Map<string, FormeRestante>();
  let ecarts = 0;
  let nonLus = 0;
  let raison: CanvasFormatUnreadableReason | undefined;
  const nonLu = (cause: CanvasFormatUnreadableReason, nombre = 1): void => {
    if (nombre === 0) return;
    nonLus += nombre;
    raison ??= cause;
  };

  for (const { nodeId } of paragraphes) {
    if ('nonRelu' in relecture) { nonLu(relecture.nonRelu); continue; }
    const lu = relus?.get(nodeId);
    if (lu === undefined) { nonLu('target_not_found_on_reread'); continue; }
    const aLaMain = resteALaMain(lu, texteLaisse.has(nodeId));
    if (aLaMain.length > 0) {
      ecarts += 1;
      if (exemples.length < CANVAS_FORMAT_DIFFERENCE_EXAMPLES_MAX) {
        const debut = lu.passages.map((passage) => passage.texte).join('').slice(0, CANVAS_FORMAT_DIFFERENCE_TEXT_MAX);
        exemples.push({ nodeId, ...(debut === '' ? {} : { text: debut }), read: aLaMain.join(', ') });
      }
      continue;
    }
    const forme: FormeRestante = {
      origin: lu.styleNom === null ? 'default' : 'style',
      ...(lu.styleNom === null ? {} : { paragraphStyleName: lu.styleNom }),
      paragraph: formeRendue(lu),
      paragraphs: 1,
    };
    const cle = JSON.stringify([forme.paragraphStyleName, forme.paragraph]);
    const connue = restes.get(cle);
    if (connue === undefined) restes.set(cle, forme);
    else connue.paragraphs += 1;
  }
  nonLu('not_written', paragraphesNonEcrits);
  for (const laisse of laisses) nonLu(laisse.raison ?? 'pending_tracked_change');

  const [premier, ...autres] = exemples;
  const [premierReste, ...autresRestes] = [...restes.values()].sort((a, b) => b.paragraphs - a.paragraphs).slice(0, CANVAS_FORMAT_REMAINING_MAX);
  const formesNonListees = restes.size - CANVAS_FORMAT_REMAINING_MAX;
  return [{
    setting: 'reset',
    requested: true,
    outcome: ecarts > 0 ? 'different' : nonLus > 0 ? 'unreadable' : 'conforming',
    ...(premier === undefined ? {} : { different: { count: ecarts, examples: [premier, ...autres] } }),
    ...(raison === undefined ? {} : { unreadable: { count: nonLus, reason: raison } }),
    ...(premierReste === undefined ? {} : { remaining: [premierReste, ...autresRestes] }),
    ...(formesNonListees > 0 ? { remainingNotListed: formesNonListees } : {}),
  }];
}
